import { useCookie } from "#app";
import { defineStore } from "pinia";

export const useSurveyWidgetStore = defineStore("surveyWidget", () => {
  const route = useRoute();
  const config = useRuntimeConfig().public;

  const allowedPages = config.allowedPages?.length ? config.allowedPages.split(",") : [];

  const allowedLocales = config.allowedLocales?.length ? config.allowedLocales.split(",") : [];

  const feedbackFormPopupTime = config.feedbackFormPopupTime || 30; // Default to 30 seconds if not set

  const dismissedUntil = useCookie("survey_dismissed_until", { maxAge: 86400 }); //cookie expires in 1 day
  const completedSurvey = useCookie("survey_completed", {
    maxAge: 60 * 60 * 24 * 365 * 10,
  }); //remember forever

  // ?slug / ?appointment_booking mark a clinic-booking journey (clinics detail
  // panel, and carried onto /tests/* and /get-started once a clinic is chosen);
  // the survey must stay out of those flows even on allowed pages.
  const bookingQueryParams = ["slug", "appointment_booking"];
  const inBookingFlow = computed(() => bookingQueryParams.some((param) => route.query[param] !== undefined));

  const timerElapsed = ref(false);
  const showWidget = computed(() => timerElapsed.value && !inBookingFlow.value);

  const delayTimer = ref(null);

  // Last arguments passed to checkVisibility, so the countdown can be
  // restarted without waiting for another route change.
  const lastPath = ref(null);
  const lastLocale = ref(null);

  const isSuppressed = () => {
    if (completedSurvey.value) return true;
    return dismissedUntil.value && new Date(dismissedUntil.value) > new Date();
  };

  const checkVisibility = (routePath, locale) => {
    lastPath.value = routePath;
    lastLocale.value = locale;

    // Cancel any timer from a previous page before deciding anything else,
    // so a countdown started on an allowed page can't fire elsewhere.
    clearTimer();
    timerElapsed.value = false;

    if (import.meta.server) return;

    if (isSuppressed()) return;

    // Only allow on specified pages (with wildcard support)
    const pageMatch = allowedPages.some((page) => {
      if (page.endsWith("/*")) {
        const prefix = page.slice(0, -1); // Remove the '*' at the end
        return routePath.startsWith(prefix);
      }
      return routePath === page;
    });
    if (!pageMatch) return;

    // Only allow on specified locales
    if (!allowedLocales.includes(locale)) return;

    delayTimer.value = setTimeout(() => {
      if (isSuppressed()) return;
      timerElapsed.value = true;
    }, feedbackFormPopupTime * 1000); // Convert seconds to milliseconds
  };

  // Entering the booking flow cancels the countdown; leaving it restarts one.
  // Closing the booking panel drops the query params without a path change
  // (navigateTo({ path: route.path })), so without this a countdown that
  // elapsed while suppressed would reveal the widget instantly, with no delay.
  watch(inBookingFlow, (busy) => {
    if (busy) {
      clearTimer();
      timerElapsed.value = false;
    } else if (lastPath.value !== null) {
      checkVisibility(lastPath.value, lastLocale.value);
    }
  });

  // The app-level route only syncs after the destination page resolves
  // (page:finish), so a countdown expiring mid-navigation could briefly show
  // the widget on a disallowed page; cancel synchronously at navigation
  // commit instead. Query-only navigations keep their countdown.
  if (import.meta.client) {
    useRouter().beforeEach((to, from) => {
      if (to.path !== from.path) {
        clearTimer();
        timerElapsed.value = false;
      }
    });
  }

  const dismissTemporarily = () => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000); //24 hours
    //const tomorrow = new Date(Date.now() + 20 * 1000); //20s for testing
    dismissedUntil.value = tomorrow.toISOString();
    timerElapsed.value = false;
    clearTimer();
  };

  const completeSurvey = () => {
    completedSurvey.value = "true";
    timerElapsed.value = false;
    clearTimer();
  };

  const clearTimer = () => {
    if (delayTimer.value) clearTimeout(delayTimer.value);
    delayTimer.value = null;
  };

  return {
    showWidget,
    checkVisibility,
    dismissTemporarily,
    completeSurvey,
  };
});
